import React, { useEffect } from 'react'
import Header from '../../components/Header'
import { Footer } from '../../components/SharedLayout'

const ProactiveMaintenanceGuide: React.FC = () => {
  useEffect(() => {
    document.title = 'Proactive Home Maintenance Guide & Full Maintenance Membership | Hudson My Home'
  }, [])

  const heading = 'rgb(15,15,15)'
  const sub = 'rgb(76,76,75)'

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24 sm:pt-28 pb-16">
        {/* Hero banner */}
        <section className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <div className="relative h-[260px] sm:h-[360px] lg:h-[440px] rounded-2xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=2000&q=80"
              alt="Warm, well‑maintained luxury home at dusk"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 100%)' }} />
            <div className="relative h-full flex flex-col justify-end p-6 sm:p-10">
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/90">Blog</p>
              <h1 className="mt-1 text-2xl sm:text-4xl font-semibold text-white">Why High‑Earning Homeowners Are Ditching the Handyman Model</h1>
              <p className="mt-2 text-sm sm:text-base max-w-3xl text-white/90">
                Learn how smart Midwest homeowners protect their homes and time with proactive maintenance. Discover the luxury of effortless living in Iowa's challenging climate.
              </p>
            </div>
          </div>
        </section>

        <article className="mx-auto max-w-[900px] px-4 sm:px-6 lg:px-8 mt-10">
          {/* Featured image inside article */}
          <figure className="mb-8">
            <img
              src="https://images.unsplash.com/photo-1519710164239-da123dc03ef4?auto=format&fit=crop&w=1800&q=80"
              alt="Technician performing preventative maintenance in a modern home"
              className="w-full h-[240px] sm:h-[360px] object-cover rounded-xl"
              loading="lazy"
            />
            <figcaption className="mt-3 text-xs text-center" style={{ color: sub }}>
              Preventative care prevents emergencies: inspections, tune‑ups, and seasonal readiness handled by your Home Director.
            </figcaption>
          </figure>

          <section className="prose prose-lg max-w-none prose-p:my-5 prose-p:leading-relaxed prose-h2:mt-10 prose-h2:mb-3 prose-h3:mt-8 prose-h3:mb-2">
            <h2>Stop Playing Defense Against Your Home</h2>
            <p>
              Every Iowa homeowner knows the drill: something breaks, you scramble to fix it, and you promise yourself you'll be more proactive next time. But "next time" never comes because you're too busy dealing with the current crisis.
            </p>
            <p>
              The smartest homeowners in the Midwest have discovered a better way. They've stopped playing defense and started living like guests in their own homes.
            </p>

            <h3>1. September Preparation Prevents January Emergencies</h3>
            <p>
              While your neighbors wait until the first cold snap to think about their furnaces, smart homeowners prepare in September. We inspect, clean, and optimize your HVAC system before everyone else realizes they need help.
            </p>
            <p>
              The result? Perfect comfort all winter while others deal with emergency service calls and premium pricing. Your system runs efficiently, your energy bills stay manageable, and your family stays comfortable no matter what Iowa's weather brings.
            </p>

            <h3>2. The Hidden Enemies of Midwest Homes</h3>
            <p>
              Dust accumulates in places you can't see. Moisture finds ways into spaces you don't check. Small leaks become big problems when you're not looking. These silent threats work against your home's value and your peace of mind.
            </p>
            <p>
              Our comprehensive inspections catch what you miss. We use thermal imaging to find insulation problems, moisture meters to detect hidden water damage, and gas detection equipment to ensure your family's safety. We eliminate problems before they eliminate your weekend plans.
            </p>

            <h3>3. Technology That Works While You Don't</h3>
            <p>
              Smart homeowners leverage technology to monitor their homes continuously. Humidity sensors prevent mold growth. Temperature monitoring protects against freeze damage. System diagnostics predict failures before they happen.
            </p>
            <p>
              We install and manage these systems for you, providing 24/7 monitoring that alerts us to issues immediately. Your home becomes smarter while your life becomes simpler.
            </p>

            <h3>4. The ROI of Professional Care</h3>
            <p>
              Every dollar invested in preventive maintenance saves five to ten dollars in emergency repairs. For luxury homes in Iowa, this translates to thousands in annual savings and tens of thousands in protected property value.
            </p>
            <p>
              But the real return isn't just financial. It's the return of your time, your peace of mind, and your ability to enjoy your home without constant worry about what might go wrong next.
            </p>

            <h3>5. Vendor Relationships That Actually Work</h3>
            <p>
              Finding reliable contractors in Iowa can feel impossible. Everyone's busy, few return calls promptly, and quality varies dramatically. Smart homeowners don't waste time with this frustration.
            </p>
            <p>
              We maintain relationships with the best service providers across all trades. When you need electrical work, plumbing repairs, or specialized services, we handle the vetting, scheduling, and oversight. You get quality work without the hassle of managing multiple vendors.
            </p>

            <h3>6. Documentation That Adds Value</h3>
            <p>
              When it's time to sell, buyers want to see maintenance records. Homes with documented professional care sell faster and command premium prices because buyers recognize the quality of ownership.
            </p>
            <p>
              We maintain comprehensive records of all services, warranties, and system specifications. This documentation becomes a valuable asset that enhances your property's marketability and your negotiating position.
            </p>

            <h2>The Midwest Advantage in Luxury Living</h2>
            <p>
              Iowa homeowners understand value. They appreciate quality. They respect expertise. This creates the perfect environment for luxury home maintenance services that deliver genuine benefits rather than empty prestige.
            </p>
            <p>
              The result is a home ownership experience that truly embodies Midwest values: practical, reliable, and focused on your family's comfort and your peace of mind.
            </p>
            <p><strong>Experience the luxury of effortless living. Your home deserves expert care, and you deserve your time back.</strong></p>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  )
}

export default ProactiveMaintenanceGuide 